/**
 * Offline Data Cache
 * Caches list data (quotes, clients, catalog, companies) for offline access
 */

const CACHE_PREFIX = 'orcamigo_cache_';
// Cached operational data is a last-known-good offline snapshot. It must not
// disappear just because it is old: when there is no connection, stale data is
// still more useful than an empty page. `cachedAt` is kept for best-effort
// storage-pressure cleanup and for future freshness decisions.
const CACHE_CLEANUP_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// Must match CompanyContext's ACTIVE_COMPANY_KEY. Business-data caches
// (quotes/clients/catalog/finance/dashboard) are namespaced by the active
// company so switching companies never shows another company's cached rows.
const ACTIVE_COMPANY_KEY = 'orcazap_active_company_id';

function activeCompanyId(): string {
  try {
    return localStorage.getItem(ACTIVE_COMPANY_KEY) || 'none';
  } catch {
    return 'none';
  }
}

interface CachedData<T> {
  userId: string;
  data: T;
  cachedAt: number;
}

// Cache keys
const KEYS = {
  QUOTES: `${CACHE_PREFIX}quotes`,
  CLIENTS: `${CACHE_PREFIX}clients`,
  CATALOG: `${CACHE_PREFIX}catalog`,
  COMPANIES: `${CACHE_PREFIX}companies`,
  DASHBOARD_STATS: `${CACHE_PREFIX}dashboard_stats`,
  AUTH_SESSION: `${CACHE_PREFIX}auth_session`,
  PROFILE: `${CACHE_PREFIX}profile`,
  FINANCE_ENTRIES: `${CACHE_PREFIX}finance_entries`,
  FINANCE_CATEGORIES: `${CACHE_PREFIX}finance_categories`,
  SERVICE_ORDERS: `${CACHE_PREFIX}service_orders`,
  APPOINTMENTS: `${CACHE_PREFIX}appointments`,
  CONTRACTS: `${CACHE_PREFIX}contracts`,
  RECEIPTS: `${CACHE_PREFIX}receipts`,
  STOCK_MOVEMENTS: `${CACHE_PREFIX}stock_movements`,
  MESSAGE_TEMPLATES: `${CACHE_PREFIX}message_templates`,
  PRODUCT_BUNDLES: `${CACHE_PREFIX}product_bundles`,
};

/**
 * Generic cache setter.
 * Returns true if the data was persisted, false if it could not be stored
 * (e.g. localStorage quota exceeded even after evicting stale caches). Callers
 * that store large payloads (offline quotes with base64 photos) can use the
 * return value to retry with a slimmed-down payload so the write isn't silently
 * dropped. See [[orcamigo-offline-sync-uuid-bug]] neighbours in saveQuoteOffline.
 */
function setCache<T>(key: string, userId: string, data: T, companyScoped = false): boolean {
  if (!userId || data === undefined) return false;

  const storageKey = companyScoped ? `${key}:${activeCompanyId()}` : key;

  const cached: CachedData<T> = {
    userId,
    data,
    cachedAt: Date.now(),
  };

  try {
    localStorage.setItem(storageKey, JSON.stringify(cached));
    return true;
  } catch (e) {
    console.error(`[offlineDataCache] Error caching ${key}:`, e);
    // If storage is full, evict old caches. Scan every namespaced cache key
    // (company-scoped keys carry a `:companyId` suffix) rather than only the
    // base KEYS, so pressure cleanup can actually free them.
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (!k || k === storageKey || !k.startsWith(CACHE_PREFIX)) continue;
        const item = localStorage.getItem(k);
        if (!item) continue;
        try {
          const parsed = JSON.parse(item);
          if (parsed && Date.now() - parsed.cachedAt > CACHE_CLEANUP_AGE_MS) {
            localStorage.removeItem(k);
          }
        } catch {
          // Not one of our JSON entries; skip.
        }
      }
      localStorage.setItem(storageKey, JSON.stringify(cached));
      return true;
    } catch {
      // Give up — storage remains too full even after eviction.
      return false;
    }
  }
}

/**
 * Generic cache getter
 */
function getCache<T>(key: string, userId: string, companyScoped = false): T | null {
  try {
    const storageKey = companyScoped ? `${key}:${activeCompanyId()}` : key;
    const cached = localStorage.getItem(storageKey);
    if (!cached) return null;

    const data: CachedData<T> = JSON.parse(cached);

    // Verify same user
    if (data.userId !== userId) return null;

    return data.data;
  } catch (e) {
    console.error(`[offlineDataCache] Error reading ${key}:`, e);
    return null;
  }
}

// ===== QUOTES ===== (company-scoped)
// Returns false when the write could not be persisted (quota). Callers storing
// offline quotes with base64 photos retry with the media stripped.
export function cacheQuotes(userId: string, quotes: any[]): boolean {
  return setCache(KEYS.QUOTES, userId, quotes, true);
}

export function getCachedQuotes(userId: string): any[] | null {
  return getCache<any[]>(KEYS.QUOTES, userId, true);
}

// ===== CLIENTS ===== (company-scoped)
export function cacheClients(userId: string, clients: any[]): void {
  setCache(KEYS.CLIENTS, userId, clients, true);
}

export function getCachedClients(userId: string): any[] | null {
  return getCache<any[]>(KEYS.CLIENTS, userId, true);
}

// ===== CATALOG (Products) ===== (company-scoped)
// Returns false when the write could not be persisted (quota). Callers that
// store an OFFLINE-CREATED product (whose photos are inline base64, not Storage
// URLs) retry with `stripCachedCatalogMedia` so the row stays visible.
export function cacheCatalog(userId: string, items: any[]): boolean {
  return setCache(KEYS.CATALOG, userId, items, true);
}

/**
 * Quota fallback for a cached catalog row: nulls the inline base64 `data:`
 * image bytes (main photo + gallery), keeping name/price/unit/stock visible.
 * The full images stay in the offline queue op, which is what syncs — so this
 * only sacrifices the local preview, never the photo itself.
 *
 * Deliberately NOT gated on `_offline_pending`: a row that came from the server
 * carries short Storage URLs (#6) and is untouched either way, while a SECOND
 * pending row (another item saved offline) is exactly the one that has to shrink
 * for the rest of the list to survive. Same reasoning as the contract/receipt
 * strips in `useOfflineSync`.
 */
export function stripCachedCatalogMedia(item: any): any {
  if (!item) return item;
  const isDataUrl = (v: any) => typeof v === 'string' && v.startsWith('data:');
  const stripPhotos = (photos: any) =>
    Array.isArray(photos)
      ? photos.map((p: any) => (isDataUrl(p?.image_url) ? { ...p, image_url: null } : p))
      : photos;
  return {
    ...item,
    image_url: isDataUrl(item.image_url) ? null : item.image_url,
    product_photos: stripPhotos(item.product_photos),
    _offline_photos: stripPhotos(item._offline_photos),
  };
}

export function getCachedCatalog(userId: string): any[] | null {
  return getCache<any[]>(KEYS.CATALOG, userId, true);
}

/**
 * Materiais inclusos (kits). Cacheados junto do catálogo porque montar o
 * orçamento a partir de um serviço-kit tem que funcionar sem rede — os
 * materiais viram `quote_items` locais como qualquer item do catálogo.
 */
export function cacheBundles(userId: string, rows: any[]): void {
  setCache(KEYS.PRODUCT_BUNDLES, userId, rows, true);
}

export function getCachedBundles(userId: string): any[] | null {
  return getCache<any[]>(KEYS.PRODUCT_BUNDLES, userId, true);
}

// ===== COMPANIES =====
export function cacheCompanies(userId: string, companies: any[]): void {
  setCache(KEYS.COMPANIES, userId, companies);
}

export function getCachedCompanies(userId: string): any[] | null {
  return getCache<any[]>(KEYS.COMPANIES, userId);
}

// ===== DASHBOARD STATS ===== (company-scoped)
export function cacheDashboardData(userId: string, data: { quotes: any[]; allQuotes: any[] }): void {
  setCache(KEYS.DASHBOARD_STATS, userId, data, true);
}

export function getCachedDashboardData(userId: string): { quotes: any[]; allQuotes: any[] } | null {
  return getCache<{ quotes: any[]; allQuotes: any[] }>(KEYS.DASHBOARD_STATS, userId, true);
}

// ===== FINANCE ENTRIES ===== (company-scoped)
export function cacheFinanceEntries(userId: string, entries: any[]): void {
  setCache(KEYS.FINANCE_ENTRIES, userId, entries, true);
}

export function getCachedFinanceEntries(userId: string): any[] | null {
  return getCache<any[]>(KEYS.FINANCE_ENTRIES, userId, true);
}

// ===== FINANCE CATEGORIES ===== (company-scoped)
export function cacheFinanceCategories(userId: string, categories: any[]): void {
  setCache(KEYS.FINANCE_CATEGORIES, userId, categories, true);
}

export function getCachedFinanceCategories(userId: string): any[] | null {
  return getCache<any[]>(KEYS.FINANCE_CATEGORIES, userId, true);
}

// ===== SERVICE ORDERS ===== (company-scoped)
// Like quotes, an offline service order carries its items/photos inline in the
// cached row (_offline_items / _offline_photos) so it renders offline. Returns
// false when the write could not be persisted (quota).
export function cacheServiceOrders(userId: string, orders: any[]): boolean {
  return setCache(KEYS.SERVICE_ORDERS, userId, orders, true);
}

export function getCachedServiceOrders(userId: string): any[] | null {
  return getCache<any[]>(KEYS.SERVICE_ORDERS, userId, true);
}

// ===== APPOINTMENTS (Agenda) ===== (company-scoped)
// Flat rows (no children). Returns false when the write could not be persisted
// (quota) — appointments are small, so this is unlikely, but kept for symmetry.
export function cacheAppointments(userId: string, appointments: any[]): boolean {
  return setCache(KEYS.APPOINTMENTS, userId, appointments, true);
}

export function getCachedAppointments(userId: string): any[] | null {
  return getCache<any[]>(KEYS.APPOINTMENTS, userId, true);
}

// ===== CONTRACTS ===== (company-scoped)
// Flat rows (no children). Only the DRAFT is offline-first; sending/signing is
// online. The provider signature (a big data URL) may be present after an online
// provider-sign; returns false when the write could not be persisted (quota) so
// the caller can retry with heavy signature data URLs stripped.
export function cacheContracts(userId: string, contracts: any[]): boolean {
  return setCache(KEYS.CONTRACTS, userId, contracts, true);
}

export function getCachedContracts(userId: string): any[] | null {
  return getCache<any[]>(KEYS.CONTRACTS, userId, true);
}

// ===== RECEIPTS (Recibos) ===== (company-scoped)
// Flat rows (no children). Offline-first over the whole lifecycle. The issuer
// signature (an optional data URL) may be present; returns false when the write
// could not be persisted (quota) so the caller can retry with it stripped.
export function cacheReceipts(userId: string, receipts: any[]): boolean {
  return setCache(KEYS.RECEIPTS, userId, receipts, true);
}

export function getCachedReceipts(userId: string): any[] | null {
  return getCache<any[]>(KEYS.RECEIPTS, userId, true);
}

// ===== STOCK MOVEMENTS (Estoque) ===== (company-scoped)
// Razão append-only. Offline, os movimentos recém-criados ficam aqui (com id
// offline_ e balance_after otimista) para exibição no histórico; ao reconectar,
// a query sobrescreve com as linhas reais do servidor.
export function cacheStockMovements(userId: string, movements: any[]): boolean {
  return setCache(KEYS.STOCK_MOVEMENTS, userId, movements, true);
}

export function getCachedStockMovements(userId: string): any[] | null {
  return getCache<any[]>(KEYS.STOCK_MOVEMENTS, userId, true);
}

// ===== MESSAGE TEMPLATES (Modelos de mensagens) ===== (company-scoped)
// Assimétrico de propósito: o GERENCIAMENTO (create/update/delete) é
// ONLINE-ONLY — modelos são configuração, não dado de negócio, então não há
// operação de fila offline para eles. A LEITURA, sim, precisa funcionar sem
// rede, porque compor a mensagem é local e o usuário tem que conseguir enviar
// offline. São poucas linhas e curtas (corpo ≤ 2000 chars), sem risco de cota.
export function cacheMessageTemplates(userId: string, templates: any[]): boolean {
  return setCache(KEYS.MESSAGE_TEMPLATES, userId, templates, true);
}

export function getCachedMessageTemplates(userId: string): any[] | null {
  return getCache<any[]>(KEYS.MESSAGE_TEMPLATES, userId, true);
}

// ===== AUTH SESSION (for offline app restart) =====
export interface CachedAuthSession {
  userId: string;
  email?: string;
  userMetadata?: Record<string, any>;
}

export function cacheAuthSession(session: CachedAuthSession): void {
  if (!session.userId) return;
  try {
    localStorage.setItem(KEYS.AUTH_SESSION, JSON.stringify({
      data: session,
      cachedAt: Date.now(),
    }));
  } catch (e) {
    console.error('[offlineDataCache] Error caching auth session:', e);
  }
}

export function getCachedAuthSession(): CachedAuthSession | null {
  try {
    const cached = localStorage.getItem(KEYS.AUTH_SESSION);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    // This is an offline identity hint, not an authentication token. Keep it
    // until an explicit logout clears it so an offline app restart can still
    // locate the correct user's cached data.
    return parsed.data;
  } catch (e) {
    console.error('[offlineDataCache] Error reading auth session:', e);
    return null;
  }
}

export function clearCachedAuthSession(): void {
  try {
    localStorage.removeItem(KEYS.AUTH_SESSION);
  } catch {
    // Ignore
  }
}

// ===== PROFILE =====
export function cacheProfile(userId: string, profile: any): void {
  setCache(KEYS.PROFILE, userId, profile);
}

export function getCachedProfile(userId: string): any | null {
  return getCache<any>(KEYS.PROFILE, userId);
}

// ===== CLEAR ALL =====
// Removes every cache entry under CACHE_PREFIX, including company-namespaced
// keys (`…:companyId`). Used on logout so one user's data never bleeds into the
// next session on a shared device.
export function clearAllDataCache(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // Fall back to removing the known base keys.
    Object.values(KEYS).forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore
      }
    });
  }
}
